import React, { useState, useEffect } from 'react';

function VacationRequest({ userInfo }) {
  const [activeTab, setActiveTab] = useState('create');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [vacationBalance, setVacationBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  
  // 申請フォームのデータ
  const [formData, setFormData] = useState({
    request_type: 'annual_leave',
    request_date: '',
    reason: '',
    client_approver: ''
  });

  // フィルター用の状態
  const [filter, setFilter] = useState({
    status: 'all',
    year: new Date().getFullYear().toString(),
    month: ''
  });

  // 休暇タイプの定義
  const vacationTypes = {
    annual_leave: '有給休暇'
  };

  // ステータスの定義
  const statusLabels = {
    pending: '申請中',
    approved: '承認済み',
    rejected: '却下',
    cancelled: '取消済み'
  };

  // ステータスのスタイル
  const getStatusStyle = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return `px-2 py-1 text-xs rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`;
  };

  // コンポーネント初期化時に申請一覧と有給残日数を取得
  useEffect(() => {
    if (activeTab === 'list') {
      fetchRequests();
    }
    if (activeTab === 'create') {
      fetchVacationBalance();
    }
  }, [activeTab, filter]);

  // 有給残日数の取得
  const fetchVacationBalance = async () => {
    try {
      setBalanceLoading(true);
      
      const response = await fetch('/api/vacation-balance/my-balance', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('有給残日数の取得に失敗しました');
      }

      const data = await response.json();
      setVacationBalance(data);
      
    } catch (err) {
      console.error('有給残日数取得エラー:', err);
      setVacationBalance({
        current_balance: 0,
        message: '有給残日数データを取得できませんでした'
      });
    } finally {
      setBalanceLoading(false);
    }
  };

  // 申請一覧の取得
  const fetchRequests = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      if (filter.status !== 'all') params.append('status', filter.status);
      if (filter.year) params.append('year', filter.year);
      if (filter.month) params.append('month', filter.month);
      
      const response = await fetch(`/api/vacation?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('申請一覧の取得に失敗しました');
      }

      const data = await response.json();
      setRequests(data.data);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // フォーム入力の変更処理
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // 申請の送信
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.request_date) {
      setError('申請日は必須項目です');
      return;
    }
    
    if (!formData.client_approver) {
      setError('クライアント承認者名は必須項目です');
      return;
    }

    // 有給残日数チェック
    if (vacationBalance && vacationBalance.current_balance <= 0) {
      setError(`有給残日数が不足しています。現在の残日数: ${vacationBalance.current_balance}日`);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await fetch('/api/vacation', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '申請の送信に失敗しました');
      }

      const result = await response.json();
      
      setMessage('有給申請を送信しました');
      
      // Pleasanter連携の結果も表示
      if (result.pleasanter && !result.pleasanter.synced) {
        setMessage(prev => prev + '（注意: Pleasanter連携でエラーが発生しました）');
      }
      
      // 申請送信後に有給残日数を再取得
      await fetchVacationBalance();
      
      // フォームをリセット
      setFormData({
        request_type: 'annual_leave',
        request_date: '',
        reason: '',
        client_approver: ''
      });

      // 3秒後にメッセージをクリア
      setTimeout(() => {
        setMessage('');
      }, 3000);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 申請の取消
  const handleCancel = async (requestId) => {
    if (!window.confirm('この申請を取り消しますか？')) {
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(`/api/vacation/${requestId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'cancelled',
          notes: '申請者による取消'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '取消に失敗しました');
      }

      setMessage('申請を取り消しました');
      fetchRequests(); // 一覧を再取得

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">有給申請</h1>

      {/* メッセージ表示 */}
      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
          {message}
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* タブナビゲーション */}
      <div className="mb-8">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('create')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'create'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            新規申請
          </button>
          <button
            onClick={() => setActiveTab('list')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'list'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            申請一覧
          </button>
        </nav>
      </div>

      {/* 新規申請タブ */}
      {activeTab === 'create' && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-6">新規有給申請</h2>
          
          {/* 有給残日数表示 */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-medium text-blue-900">有給残日数</h3>
              <button 
                onClick={fetchVacationBalance}
                disabled={balanceLoading}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                title="最新の有給残日数を取得します"
              >
                {balanceLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                    更新中...
                  </>
                ) : (
                  <>
                    🔄 最新データを取得
                  </>
                )}
              </button>
            </div>
            {balanceLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                <span className="text-blue-700">残日数を確認中...</span>
              </div>
            ) : vacationBalance ? (
              <div>
                <div className="text-2xl font-bold text-blue-900">
                  {vacationBalance.current_balance !== undefined ? `${vacationBalance.current_balance}日` : '不明'}
                </div>
                {vacationBalance.latest && (
                  <div className="text-sm text-blue-700 mt-1">
                    {vacationBalance.latest.grant_year}年度 
                    {vacationBalance.latest.expiry_date && (
                      ` (有効期限: ${new Date(vacationBalance.latest.expiry_date).toLocaleDateString('ja-JP')})`
                    )}
                  </div>
                )}
                {vacationBalance.current_balance <= 0 && (
                  <div className="text-red-600 font-medium mt-2">
                    ⚠️ 有給残日数が不足しているため申請できません
                  </div>
                )}
              </div>
            ) : (
              <div className="text-orange-700">
                有給残日数データを取得できませんでした
              </div>
            )}
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 申請種別（固定） */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                申請種別
              </label>
              <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
                有給休暇
              </div>
            </div>

            {/* 申請日 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                申請日 *
              </label>
              <input
                type="date"
                name="request_date"
                value={formData.request_date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* 理由 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                申請理由
              </label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="有給取得の理由を入力してください（任意）"
              />
            </div>

            {/* クライアント承認者名 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                クライアント承認者名 *
              </label>
              <input
                type="text"
                name="client_approver"
                value={formData.client_approver}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="承認者の名前を入力してください"
                required
              />
            </div>

            {/* 送信ボタン */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading || (vacationBalance && vacationBalance.current_balance <= 0)}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '送信中...' : '申請を送信'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 申請一覧タブ */}
      {activeTab === 'list' && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-6">申請一覧</h2>
          
          {/* フィルター */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ステータス
              </label>
              <select
                value={filter.status}
                onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">全て</option>
                {Object.entries(statusLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                年
              </label>
              <select
                value={filter.year}
                onChange={(e) => setFilter(prev => ({ ...prev, year: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {[...Array(5)].map((_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return (
                    <option key={year} value={year.toString()}>{year}年</option>
                  );
                })}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                月
              </label>
              <select
                value={filter.month}
                onChange={(e) => setFilter(prev => ({ ...prev, month: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">全ての月</option>
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={(i + 1).toString()}>{i + 1}月</option>
                ))}
              </select>
            </div>
          </div>

          {/* 申請リスト */}
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">読み込み中...</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">申請がありません</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      申請日
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      種別
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      期間
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      日数
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ステータス
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pleasanter
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {requests.map((request) => (
                    <tr key={request.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(request.created_at).toLocaleDateString('ja-JP')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {vacationTypes[request.request_type]}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(request.start_date).toLocaleDateString('ja-JP')} ～ {new Date(request.end_date).toLocaleDateString('ja-JP')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {request.days_count}日
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusStyle(request.status)}>
                          {statusLabels[request.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {request.pleasanter_sync_status === 'synced' ? (
                          <span className="text-green-600">✓ 連携済み</span>
                        ) : request.pleasanter_sync_status === 'failed' ? (
                          <span className="text-red-600">✗ 失敗</span>
                        ) : (
                          <span className="text-yellow-600">⏳ 待機中</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {request.status === 'pending' && (
                          <button
                            onClick={() => handleCancel(request.id)}
                            className="text-red-600 hover:text-red-900"
                            disabled={loading}
                          >
                            取消
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default VacationRequest; 